import { useInvoiceFormContext } from '@/components/Fiscal/Invoice/InvoiceFormProvider';
import { Button, Col, Dropdown, Form, Menu, Modal, Row, Tooltip } from 'antd';
import {
  useCancelInvoice,
  useCancelNationalNfs,
} from '@/core/domain/invoice/invoice/invoice.hooks';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import Authorities from '@/types/Authority';
import { useDefaultMinLengthFieldError, useDisclosure } from '@/hooks';
import {
  CancelInvoiceCode,
  CancelInvoiceRequest,
} from '@/core/domain/invoice/invoice/Invoice.types';
import { FloatingFormItem, Group, InputText } from '@/components/_common';
import invoiceHandleError from '@/core/domain/invoiceTransmissionHub/invoice.error.transmit';
import ModalLoadingOverlay from '@/components/_common/ModalLoadingOverlay';
import CancellationCodeSelect from '@/components/_common/CancellationCodeSelect';
import { ChevronDown, Info } from 'react-feather';

export function InvoiceCancelTecnoSpeed() {
  const { isNfs, invoiceId, cancellationCode, activeNfseNacional } =
    useInvoiceFormContext();

  const defaultMinLenghtFieldError = useDefaultMinLengthFieldError();

  const [formModal] = Form.useForm<{
    justification: 'string';
    code: CancelInvoiceCode;
  }>();

  const [modalVisible, setModalVisible] = useDisclosure(false);

  const cancelInvoice = useCancelInvoice();
  const cancelNationalNfs = useCancelNationalNfs();

  const handleSubmit = async () => {
    try {
      const { justification, code } = await formModal.validateFields();

      const obj: CancelInvoiceRequest = {
        invoiceId: Number(invoiceId),
        justification,
        code: code,
      };

      await cancelInvoice.mutateAsync(obj);

      setModalVisible.toggle();
    } catch (err) {
      invoiceHandleError(err, 'Erro ao cancelar nota fiscal.');
    }
  };

  const CANCEL_STATUS_WARNING = (
    <>
      <p>
        Esta ação irá alterar a situação da nota fiscal para{' '}
        <strong>"Cancelada"</strong> somente no Ultracar.
      </p>

      <p>
        Como o status oficial já está cancelado no Ambiente Nacional, não há
        sincronização disponível.
      </p>

      <p>
        Caso a nota fiscal seja consultada novamente, o status será atualizado
        para <strong>"Autorizada"</strong>.
      </p>
    </>
  );

  const menu = (
    <Menu>
      <Menu.Item
        key="cancel-status"
        onClick={() => {
          Modal.confirm({
            title: 'Confirmar alteração de status',
            content: (
              <>
                {CANCEL_STATUS_WARNING}

                <p>Deseja continuar?</p>
              </>
            ),
            onOk: () => {
              if (invoiceId) cancelNationalNfs.mutateAsync(Number(invoiceId));
            },
            okText: 'Alterar',
          });
        }}
      >
        <Group gap={6}>
          <span>Mudar situação para Cancelada</span>
          <Tooltip placement="right" title={CANCEL_STATUS_WARNING}>
            <Info size={16} />
          </Tooltip>
        </Group>
      </Menu.Item>
    </Menu>
  );

  return (
    <>
      {cancelInvoice.isLoading && cancelNationalNfs.isLoading && (
        <ModalLoadingOverlay
          messages={['Cancelando nota...', 'Validando dados da nota...']}
        />
      )}

      <Button.Group style={{ width: '100%' }}>
        <Button
          danger
          block
          type="primary"
          onClick={() => setModalVisible.toggle()}
          disabled={!hasPermissions([Authorities.FISCAL_INVOICE_CANCEL])}
        >
          Cancelar
        </Button>

        {isNfs && activeNfseNacional && (
          <Dropdown overlay={menu} placement="bottomRight" trigger={['click']}>
            <Button danger type="primary" style={{ padding: '8px' }}>
              <ChevronDown size={16} />
            </Button>
          </Dropdown>
        )}
      </Button.Group>

      <Modal
        title="Cancelamento Nota Fiscal"
        open={modalVisible}
        onOk={handleSubmit}
        cancelButtonProps={{
          disabled: cancelInvoice.isLoading,
        }}
        closable={cancelInvoice.isLoading}
        maskClosable={cancelInvoice.isLoading}
        onCancel={() => setModalVisible.toggle()}
      >
        <Form form={formModal}>
          <Row>
            {!!cancellationCode?.length && isNfs ? (
              <CancellationCodeSelect />
            ) : (
              <Col span={24}>
                <FloatingFormItem
                  label="Justificativa do cancelamento da nota fiscal"
                  name="justification"
                  rules={[defaultMinLenghtFieldError(15)]}
                >
                  <InputText />
                </FloatingFormItem>
              </Col>
            )}
          </Row>
        </Form>
      </Modal>
    </>
  );
}
